import React, { useMemo, useState } from "react";
import { CalendarRange, CreditCard, Download, Euro, ReceiptText } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import PartnerMetricCard from "@/components/partners/PartnerMetricCard";
import { BillingPeriod, BillableLead, LEAD_PRICE, filterLeadsByPeriod, groupLeadsByMonth, toGreekMonth } from "@/utils/billingUtils";
import { SERVICE_LABELS, parsePortalDate } from "@/types/partnerPortal";
import { cn } from "@/lib/utils";

interface PartnerBillingTabProps {
  leads: BillableLead[];
  partnerName: string;
}

const PERIODS: Array<{ value: BillingPeriod; label: string; short: string }> = [
  { value: "week", label: "Εβδομάδα", short: "Εβδ." },
  { value: "month", label: "Μήνας", short: "Μήνας" },
  { value: "quarter", label: "Τρίμηνο", short: "3μηνο" },
  { value: "year", label: "Έτος", short: "Έτος" },
  { value: "all", label: "Όλη η περίοδος", short: "Όλα" },
  { value: "custom", label: "Προσαρμοσμένο", short: "Ημ/νίες" },
];

const inputDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const BillingTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) => {
  if (!active || !payload?.length) return null;
  const count = payload[0].value;
  return <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs shadow-lg"><p className="font-semibold text-slate-700">{label}</p><p className="mt-1 font-bold text-emerald-700">{count} leads · {count * LEAD_PRICE}€</p></div>;
};

const csvCell = (value: string | number) => {
  const raw = String(value);
  const safe = /^[=+@-]/.test(raw) ? `'${raw}` : raw;
  return `"${safe.replace(/"/g, '""')}"`;
};

const PartnerBillingTab: React.FC<PartnerBillingTabProps> = ({ leads, partnerName }) => {
  const [period, setPeriod] = useState<BillingPeriod>("month");
  const [customFrom, setCustomFrom] = useState(() => {
    const now = new Date();
    return inputDate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [customTo, setCustomTo] = useState(inputDate(new Date()));

  const fromDate = period === "custom" && customFrom ? new Date(`${customFrom}T00:00:00`) : null;
  const toDate = period === "custom" && customTo ? new Date(`${customTo}T00:00:00`) : null;
  const periodLeads = filterLeadsByPeriod(leads, period, fromDate, toDate);
  const sortedLeads = useMemo(() => [...periodLeads].sort((a, b) => parsePortalDate(b.createdAt).getTime() - parsePortalDate(a.createdAt).getTime()), [periodLeads]);
  const monthlyData = useMemo(() => groupLeadsByMonth(leads).map((item) => ({ ...item, month: toGreekMonth(item.month) })), [leads]);
  const totalAmount = periodLeads.length * LEAD_PRICE;

  const exportCsv = () => {
    const rows = [
      ["Ημερομηνία", "Όνομα", "Υπηρεσία", "Χρέωση"],
      ...sortedLeads.map((lead) => [format(parsePortalDate(lead.createdAt), "dd/MM/yyyy"), lead.name, SERVICE_LABELS[lead.service] || lead.service, `${LEAD_PRICE}€`]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(csvCell).join(";")).join("\n")}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `xreoseis-${partnerName.replace(/\s+/g, "-").toLowerCase()}-${inputDate(new Date())}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <PartnerMetricCard label="Leads περιόδου" value={periodLeads.length} hint="Καταχωρημένες αναθέσεις" icon={<ReceiptText className="h-5 w-5" />} tone="blue" />
        <PartnerMetricCard label="Υπολογιζόμενο ποσό" value={`${totalAmount}€`} hint="Βάσει των leads περιόδου" icon={<Euro className="h-5 w-5" />} tone="emerald" />
        <div className="col-span-2 lg:col-span-1"><PartnerMetricCard label="Τιμή ανά lead" value={`${LEAD_PRICE}€`} hint="Τρέχουσα τιμή πλατφόρμας" icon={<CreditCard className="h-5 w-5" />} tone="slate" /></div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="font-bold text-slate-950">Επιλογή περιόδου</h2>
            <p className="mt-1 text-xs text-slate-500">Οι ημερομηνίες περιλαμβάνονται ολόκληρες.</p>
          </div>
          <div className="flex max-w-full gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1">
            {PERIODS.map((item) => (
              <button key={item.value} type="button" onClick={() => setPeriod(item.value)} className={cn("h-11 shrink-0 rounded-lg px-3 text-xs font-semibold", period === item.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white")}>
                <span className="sm:hidden">{item.short}</span><span className="hidden sm:inline">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
        {period === "custom" && (
          <div className="mt-4 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[auto_1fr_1fr] sm:items-end">
            <CalendarRange className="hidden h-5 w-5 text-slate-400 sm:mb-3 sm:block" />
            <label className="text-xs font-semibold text-slate-600">Από<input type="date" value={customFrom} max={customTo} onChange={(event) => setCustomFrom(event.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800" /></label>
            <label className="text-xs font-semibold text-slate-600">Έως<input type="date" value={customTo} min={customFrom} max={inputDate(new Date())} onChange={(event) => setCustomTo(event.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800" /></label>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <h2 className="font-bold text-slate-950">Leads ανά μήνα</h2>
        <p className="mt-1 text-xs text-slate-500">Οι τελευταίοι 12 μήνες, ανεξάρτητα από το φίλτρο περιόδου.</p>
        <div className="mt-4 h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={{ top: 4, right: 2, left: -24, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} minTickGap={18} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} />
              <Tooltip content={<BillingTooltip />} />
              <Bar dataKey="count" fill="#059669" radius={[5, 5, 0, 0]} maxBarSize={38} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
          <div><h2 className="font-bold text-slate-950">Ανάλυση περιόδου</h2><p className="mt-1 text-xs text-slate-500">{periodLeads.length} leads · {totalAmount}€</p></div>
          <Button variant="outline" onClick={exportCsv} disabled={!periodLeads.length} className="h-11 rounded-xl"><Download className="mr-2 h-4 w-4" /> <span className="hidden sm:inline">Εξαγωγή </span>CSV</Button>
        </div>
        {!sortedLeads.length ? (
          <p className="px-4 py-10 text-center text-sm text-slate-500">Δεν υπάρχουν leads στην επιλεγμένη περίοδο.</p>
        ) : (
          <>
            <div className="divide-y divide-slate-100 sm:hidden">
              {sortedLeads.map((lead) => (
                <div key={lead.id} className="flex items-start justify-between gap-3 px-4 py-4">
                  <div className="min-w-0"><p className="truncate text-sm font-bold text-slate-900">{lead.name}</p><p className="mt-1 text-xs text-slate-500">{format(parsePortalDate(lead.createdAt), "dd/MM/yyyy")} · {SERVICE_LABELS[lead.service] || lead.service}</p></div>
                  <span className="shrink-0 text-sm font-bold text-emerald-700">{LEAD_PRICE}€</span>
                </div>
              ))}
            </div>
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-5 py-3 font-semibold">Ημερομηνία</th><th className="px-5 py-3 font-semibold">Όνομα</th><th className="px-5 py-3 font-semibold">Υπηρεσία</th><th className="px-5 py-3 text-right font-semibold">Χρέωση</th></tr></thead>
                <tbody className="divide-y divide-slate-100">{sortedLeads.map((lead) => <tr key={lead.id}><td className="whitespace-nowrap px-5 py-3 text-slate-500">{format(parsePortalDate(lead.createdAt), "dd/MM/yyyy")}</td><td className="px-5 py-3 font-semibold text-slate-900">{lead.name}</td><td className="px-5 py-3 text-slate-600">{SERVICE_LABELS[lead.service] || lead.service}</td><td className="px-5 py-3 text-right font-bold text-emerald-700">{LEAD_PRICE}€</td></tr>)}</tbody>
                <tfoot><tr className="border-t-2 border-slate-200 bg-slate-50"><td colSpan={3} className="px-5 py-3 font-bold text-slate-800">Σύνολο</td><td className="px-5 py-3 text-right font-bold text-emerald-700">{totalAmount}€</td></tr></tfoot>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default PartnerBillingTab;
